import React, { useState, useContext, useEffect, useRef } from 'react'
import { Button } from 'syngenta-react-components'
import DetailView from '../../../dpe-usco/components/Details/DetailView'
import { useParams } from 'react-router-dom'
import { GlobalContext, AuthContext } from '../../../services/context'
import ProductionPlanDetailWidget from './ProductionPlanDetailWidget'
import ViewProdPlanDeliveryWidget from './ViewProdPlanDeliveryWidget'
import { getScaleTickets, getProductionPlan as getParentOrChildPlan } from '../../../services/apiHelper'
import useApiHelper from '../../../services/useApiHelper'
import LegalTemplatesTableWidget from './LegalTemplatesTableWidget'
import moment from 'moment'

const generateMonthMap = (year, deliveryType) => {
  const startDate = year ? `${year}-09-01` : new Date().getFullYear() + '-09-01'
  const momentDate = moment(startDate)
  const monthMap = new Map()
  for (let i = 0; i < 13; i++) {
    const newDate = momentDate.clone().add(i, 'month')
    const delivery_month = newDate.format('MMM-YY')
    const month = newDate.format('MMMM').toLowerCase()
    const year = newDate.format('YYYY')
    monthMap.set(delivery_month, {
      delivery_month,
      month,
      year,
      available_acres: 0,
      available_units: 0,
      contract_acres: 0,
      contract_units: 0,
      delivery_type: deliveryType,
      harvest_bushels: 0,
      premium: 0
    })
  }
  return monthMap
}

const ProductionPlanDetailView = () => {
  const { dispatch, trackEvent } = useContext(GlobalContext)
  const { hasAccess } = useContext(AuthContext)
  const [totalReceived, setTotalReceived] = useState(0)
  const [hasPermission, setHasPermission] = useState(false)
  const params = useParams()
  const { production_plan_id, company_id } = params || {}
  const [productionPlanId] = useState(production_plan_id)
  const [productionPlanData, setProductionPlanData] = useState({
    aph:0,
    created: '',
    year: '',
    contract_type: '',
    delivery_location: '',
    delivery_address: ''
  })
  const [monthlyUnits, setMonthlyUnits] = useState()
  const [deliveryStatus, setDeliveryStatus] = useState('LOADING')
  const [
    { data: prodPlanData, status: prodPlanStatus },
    getProductionPlan
  ] = useApiHelper('getProductionPlan')
  const [
    { data: childrenProdPlansData, status: childrenProdPlansStatus },
    getChildrenProductionPlans
  ] = useApiHelper('getProductionPlan')
  const [
    { data: contractTemplatesData, status: templateStatus },
    getContractTemplates
  ] = useApiHelper('getContractTemplates')
  const [
    { data: committedUnitsData, status: committedUnitsStatus },
    getCommittedUnits
  ] = useApiHelper('getCommittedUnits')

  const canceled = useRef(false)
  useEffect(() => {
    return () => {
      canceled.current = true
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      if (!canceled.current) {
        getProductionPlan(production_plan_id)
        getCommittedUnits(production_plan_id)
        getChildrenProductionPlans(production_plan_id, 'parent')
      }
    }
    if (production_plan_id) {
      init()
    }
  }, [
    getChildrenProductionPlans,
    getCommittedUnits,
    getProductionPlan,
    production_plan_id
  ])

  useEffect(() => {
    if (prodPlanStatus === null) {
      getContractTemplates(prodPlanData.external_reference_id, null , production_plan_id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContractTemplates, prodPlanData, prodPlanStatus])

  useEffect(() => {
    const formatUnits = (
      contractYear,
      prodPlanMonthlyUnits,
      aph,
      premium_type,
      committedUnits = [],
      childrenProdPlansData = []
    ) => {
      const deliveryTypes = ['grain', 'silage', 'earlage', 'high-moisture']
      const workingObj = {}
      let allChildMonthlyUnits = []
      if (childrenProdPlansData?.length) {
        childrenProdPlansData.forEach(childProdPlan => {
          allChildMonthlyUnits = [
            ...allChildMonthlyUnits,
            ...childProdPlan.monthly_units
          ]
        })
      }
      deliveryTypes.forEach(deliveryType => {
        const filteredCommittedUnits = committedUnits.filter(comUnit => {
          return comUnit.delivery_type === deliveryType
        })
        const committedUnitsMap = generateMonthMap(contractYear, deliveryType)
        if (filteredCommittedUnits.length) {
          filteredCommittedUnits.forEach(comUnit => {
            let YearExtend = false
            let contractYearNext = parseInt(contractYear) + 1
            if((comUnit.delivery_year < contractYear) || (comUnit.delivery_year > contractYearNext)){
              YearExtend = true
            } else {
              if(comUnit.delivery_year === contractYear && (comUnit.delivery_month.charAt(0)!=='s' || comUnit.delivery_month.charAt(0)!=='o' || comUnit.delivery_month.charAt(0)!=='n' || comUnit.delivery_month.charAt(0)!=='d')){
                YearExtend = true
              }
              if(comUnit.delivery_year === contractYearNext &&  (comUnit.delivery_month.charAt(0)==='o' || comUnit.delivery_month.charAt(0)==='n' || comUnit.delivery_month.charAt(0)==='d')){
                YearExtend = true
              }
            }
            if(YearExtend === true) {
              if(comUnit.delivery_month.charAt(0)==='s' || comUnit.delivery_month.charAt(0)==='o' || comUnit.delivery_month.charAt(0)==='n' || comUnit.delivery_month.charAt(0)==='d')
                comUnit.delivery_year = contractYear
              else
                comUnit.delivery_year = contractYearNext
            }
            const delivery_month = moment(
              `${comUnit.delivery_month}, 1 ${comUnit.delivery_year}`
            ).format('MMM-YY')
            const current = committedUnitsMap.get(delivery_month)
            if (current) {
              const totalContractedUnits =
                parseInt(current.contract_units) +
                parseInt(comUnit.contract_units)
              const totalContractedAcres = parseInt(current.contract_acres) +
              parseInt(comUnit.contract_acres)
              const totalHarvestBushels =
                parseInt(current.harvest_bushels) +
                parseInt(comUnit.harvest_bushels)
              const returnable = {
                ...current,
                contract_acres: totalContractedAcres,
                contract_units: totalContractedUnits,
                harvest_bushels: totalHarvestBushels
              }
              committedUnitsMap.set(delivery_month, returnable)
            } else {
              committedUnitsMap.set(delivery_month, comUnit)
            }
          })
        }
        workingObj[deliveryType] = committedUnitsMap
      })

      if (prodPlanMonthlyUnits.length) {
        deliveryTypes.forEach(deliveryType => {
          const filteredUnits = prodPlanMonthlyUnits.filter(comUnit => {
            return comUnit.end_product_type === deliveryType
          })
          const filteredChildUnits = allChildMonthlyUnits.filter(
            monthlyUnits => {
              return monthlyUnits.end_product_type === deliveryType
            }
          )
          const committedUnitsMap = workingObj[deliveryType]
          if (filteredUnits.length) {
            filteredUnits.forEach(comUnit => {
              const delivery_month = moment(
                `${comUnit.month}, 1 ${comUnit.year}`
              ).format('MMM-YY')
              const current = committedUnitsMap.get(delivery_month)
              const premiumType = premium_type === 'bushels' ? 'bu' : 'ac'
              const returnable = {
                ...current,
                premium: parseFloat(comUnit.premium).toFixed(2)+'/'+premiumType,
                available_acres:
                  comUnit.available_acres - current.contract_acres,
                available_units:
                  comUnit.available_units - current.contract_units,
                total_acres: comUnit.available_acres,
                total_units: comUnit.available_units
              }
              committedUnitsMap.set(delivery_month, returnable)
            })
          }
          // eslint-disable-next-line no-constant-condition
          if (filteredChildUnits.length && false) {
            filteredChildUnits.forEach(monthlyUnit => {
              const delivery_month = moment(
                `${monthlyUnit.month}, 1 ${monthlyUnit.year}`
              ).format('MMM-YY')
              const current = committedUnitsMap.get(delivery_month)
              if (current) {

                const totalContractedUnits =
                  current.contract_units + parseInt(monthlyUnit.child_plan_units)
                const totalContractedAcres = current.contract_acres +
                parseInt(monthlyUnit.child_plan_acres)

                const totalAvailableAcres =
                current.total_acres - totalContractedAcres
                const totalAvailableUnits =
                parseInt(current.total_units) -
                parseInt(totalContractedUnits)

                const returnable = {
                  ...current,
                  available_acres: totalAvailableAcres,
                  available_units: totalAvailableUnits,
                  contract_acres: totalContractedAcres,
                  contract_units: totalContractedUnits
                }

                committedUnitsMap.set(delivery_month, returnable)
              } else {
                committedUnitsMap.set(delivery_month, {
                  ...current
                })
              }
            })
          }
        })
      }

      const returnObj = {}
      deliveryTypes.forEach(deliveryType => {
        let typeArray = []
        const currentMap = workingObj[deliveryType]
        currentMap.forEach(comUnit => {
          typeArray.push(comUnit)
        })
        typeArray = typeArray.map(obj => {
          const totalAcres = parseFloat(obj.total_acres) || 0
          const totalUnits = parseFloat(obj.total_units) || 0

          const availableAcres = totalAcres - obj.contract_acres
          const availableUnits = totalUnits - obj.contract_units

          return {
            ...obj,
            available_acres: availableAcres,
            available_units: availableUnits
          }
        })
        returnObj[deliveryType] = typeArray
      })
      return returnObj
    }
    if (
      (!committedUnitsStatus || committedUnitsStatus === 'EMPTY') &&
      (!childrenProdPlansStatus || childrenProdPlansStatus === 'EMPTY') &&
      prodPlanData?.monthly_units
    ) {
      const returnable = formatUnits(
        prodPlanData.contract_year,
        prodPlanData.monthly_units,
        prodPlanData.aph,
        prodPlanData.premium_type,
        committedUnitsData,
        childrenProdPlansData
      )
      if (!canceled.current) {
        setMonthlyUnits(returnable)
        setDeliveryStatus()
      }
    }
  }, [
    childrenProdPlansData,
    childrenProdPlansStatus,
    committedUnitsData,
    committedUnitsStatus,
    prodPlanData,
    prodPlanStatus
  ])

  const [parentCompanyName, setParentCompanyName] = useState('')
  const [childCompanyName, setChildCompanyName] = useState('')
  useEffect(() => {
    if(prodPlanData){
      if(prodPlanData.parent_production_plan_id !== 'null'){
        getParentOrChildPlan(prodPlanData.parent_production_plan_id).then(res => {
          setParentCompanyName(res.company_name)
        })
      } else {
        getParentOrChildPlan(prodPlanData.production_plan_id, 'parent').then(res => {
          if(!res) return

          let children = res.map(item => item.company_name)
          children = children.filter(function(item, pos) {
            return children.indexOf(item) === pos
          })
          setChildCompanyName(children.join(','))
        })
      }
    }
  }, [prodPlanData])

  useEffect(() => {
    const checkForEditAccess = external_reference_id => {
      const access = hasAccess(external_reference_id, 'create-production-plan')
      if (!canceled.current) {
        setHasPermission(access === 'SUCCESS')
      }
    }
    let tempObj = {}
    if (prodPlanData) {
      tempObj.aph=prodPlanData.aph
      tempObj.created = moment(prodPlanData.created).format('MMMM Do YYYY')
      tempObj.year = prodPlanData.contract_year
      tempObj.contract_type =
        prodPlanData.production_plan_extra.contract_type_enum
      tempObj.delivery_location = prodPlanData.delivery_location.location_name
      tempObj.delivery_address = `${prodPlanData.delivery_location.location_address1}, ${prodPlanData.delivery_location.location_city}, ${prodPlanData.delivery_location.location_state} ${prodPlanData.delivery_location.location_zipcode}, ${prodPlanData.delivery_location.location_country}`
      tempObj.inventory_code = prodPlanData.inventory_code
      if(parentCompanyName){
        tempObj.parent_plan = parentCompanyName
        tempObj.child_plan = 'Yes'
      } else if(childCompanyName){
        tempObj.parent_plan = 'Yes'
        tempObj.child_plan = childCompanyName
      } else {
        tempObj.parent_plan = 'No'
        tempObj.child_plan = 'No'
      }
      if (!canceled.current) {
        setProductionPlanData(tempObj)
        checkForEditAccess(prodPlanData.external_reference_id)
      }
    }
  }, [hasAccess, prodPlanData, setHasPermission, parentCompanyName, childCompanyName])

  useEffect(() => {
    if(prodPlanData) {
      let totalNetQtyMap = 0
      getScaleTickets(production_plan_id, null).then(scaleTickets => {
        let totalNetQty = 0
        if (scaleTickets.length) {
          scaleTickets.forEach(ticket => {
            let selectedNetQty = 0
            if (ticket.net_quantity && typeof ticket.net_quantity === 'string') {
              selectedNetQty = parseFloat(ticket.net_quantity.replace(/,/g, ''))
            }
            if (ticket.net_quantity && typeof ticket.net_quantity === 'number') {
              selectedNetQty = ticket.net_quantity
            }
            totalNetQty = totalNetQty + selectedNetQty
          })
        }
        totalNetQtyMap = totalNetQty
      }).then(() => {
        setTotalReceived(totalNetQtyMap)
      })
    }
  }, [prodPlanData, production_plan_id])

  const handleEditButton = () => {
    trackEvent('edit_production_plan_clicked', {
      companyId: company_id,
      productionPlanId: production_plan_id
    })
    dispatch({
      type: 'GO_TO_EDIT_PRODUCTION_PLAN',
      payload: { company_id, production_plan_id: productionPlanId }
    })
  }

  let detailViewButton = null
  if (hasPermission) {
    detailViewButton = (
      <Button
        styleType="success"
        customClassName="mb10"
        onClick={handleEditButton}
      >
        Edit plan
      </Button>
    )
  }
  return (
    <DetailView button={detailViewButton}>
      <>
        <ProductionPlanDetailWidget
          prodPlanData={productionPlanData}
          status={prodPlanStatus}
        />
        <ViewProdPlanDeliveryWidget
          aph = {productionPlanData.aph}
          contractType={prodPlanData?.production_plan_extra?.contract_type_enum}
          monthlyUnits={monthlyUnits}
          unitType={prodPlanData?.unit_type}
          premium_type={prodPlanData?.premium_type}
          contractYear={prodPlanData?.contract_year}
          initialAcres={prodPlanData?.total_acres}
          initialUnits={prodPlanData?.total_units}
          status={deliveryStatus}
          totalReceived={totalReceived}
        />
        <LegalTemplatesTableWidget
          data={contractTemplatesData}
          status={templateStatus}
          company_id={company_id}
        />
      </>
      <></>
    </DetailView>
  )
}

export default ProductionPlanDetailView
